"use client"

import Link from "next/link"
import { usePathname } from "next/navigation"
import { useState } from "react"
import { ModuleStyle } from "@/modules/module-style"

/**
 * The routing bubble: a few fixed taps to the right offer page.
 *
 * DETERMINISTIC ON PURPOSE. Owner: "if the entire point of the button is to
 * route them to the right product why don't we just have a deterministic
 * bubble? it's not really a chat and can just route them to the right page."
 *
 * There is no model and no free-text box, so it cannot invent a price or an
 * inclusion. That was the one risk that mattered when this was going to be a
 * bought LLM widget, and here it is designed out rather than mitigated. Never
 * add a text input: that is the change that would drag an LLM back in.
 *
 * Shape follows the published product-finder rule, three to six questions with
 * every answer changing the result. Our nine offerings separate in two or three
 * taps because the first question is the header's own split.
 */

type Step = { q: string; a: [string, string][] }

// COPY IS THE OWNER'S, given verbatim (2026-08-02). He rejected the previous
// wording as sounding "like it was written by someone trying to sound like a UX
// writer instead of a human", and the fix is literalness: the exact words a
// customer is already thinking, no metaphors, no clever questions. The options
// lead with HIS goal rather than our product name, which is why step one says
// "Learn to meet women myself" and not "Coaching".
//
// Do not reword any of this. See context/voice-and-copy.md.
const STEPS: Record<string, Step> = {
  start: {
    q: "What are you looking for?",
    a: [
      ["Learn to meet women myself", "coach"],
      ["Have someone manage my dating apps", "dfy"],
      ["I have a question", "__talk"],
    ],
  },
  coach: {
    q: "How would you like to work with us?",
    a: [
      ["In Person", "/bootcamp"],
      ["Online", "coach_online"],
    ],
  },
  coach_online: {
    q: "How often?",
    a: [
      ["Every week", "/mentorship"],
      // NAMED, not described. He asked to "add the online dating mentorship as
      // one of the coaching options" and it was already here, reachable, and
      // labelled "Weekly, for the dating apps", which names no program. An
      // option a man cannot recognise is not an option he has.
      ["Online Dating Mentorship", "/online-dating-mentorship"],
      ["When I need it", "/calls"],
    ],
  },
  dfy: {
    q: "What do you need help with?",
    a: [
      ["Photos", "/ai-photos"],
      ["Photos + Profile", "/profile-makeover"],
      ["We Run Your Dating Apps", "/online-dating-concierge"],
    ],
  },
}

const NAME: Record<string, string> = {
  "/mentorship": "Mentorship Program",
  "/online-dating-mentorship": "Online Dating Mentorship",
  "/bootcamp": "Bootcamp",
  "/calls": "1-on-1 Calls",
  "/ai-photos": "AI Photos",
  "/profile-makeover": "Profile Makeover",
  "/online-dating-concierge": "Online Dating Concierge",
}

// Design notes for CSS below. They live here, outside the template literal,
// so the minifier drops them and they never reach the public client JS chunk.
//
// Above `@media (max-width:639px)`:
//   GONE ON A PHONE. His words: "right now on mobile we have the find yoru
//   program bottom sticky bar and the question mark. let's retire the question
//   from mobile but keep it on desktop because right now on mobile they clash."
//   639px is the bar's own breakpoint (Tailwind sm:hidden), so exactly one of the
//   two is on screen at every width and there is no band where both or neither
//   shows.
//
// Above `.ca-bub .tab .d`:
//   A question mark, never a bare dot. Owner, twice: the dot "is confusing on
//   mobile, make it a more universally understood help symbol." A coloured dot
//   signals a notification or a status light, not "press this for help", so it
//   reads as decoration and gets ignored. The "?" is the one glyph every visitor
//   already knows. It is drawn as an SVG rather than a text character so it is
//   the same mark on every platform, and it shows on desktop AND mobile, since
//   the dot was doing the same non-job in the pill.
//
// Above `.ca-bub .panel`:
//   The panel is as wide as its widest line, never a fixed width. It was pinned
//   at 284px, so "Have someone manage my dating apps" folded onto a second line
//   to fit it. Sizing to content inverts that: the wording sets the width, and a
//   longer option widens the panel instead of wrapping inside it. That is what
//   makes it stay fixed, because the options get reworded and a hardcoded width
//   would only go stale again. The floor keeps the two-word steps from
//   collapsing to a stub; the ceiling keeps it inside a phone.
//
// Above `.ca-bub .panel`:
//   Keep sizing to content here too — re-pinning a width on the phone is
//   exactly where the wrap showed up. Only the ceiling tightens.
//
// Above `.ca-bub .tab`:
//   The label stays on the phone. Collapsed to a bare question mark it read as
//   decoration rather than a control, so nobody pressed it. The pill shrinks
//   instead of shedding its words: smaller type and tighter padding, and it
//   still clears the 44px minimum tap height.
const CSS = `
.ca-bub{position:fixed;left:22px;bottom:22px;z-index:40}

@media (max-width:639px){ .ca-bub{display:none} }
.ca-bub .tab{display:flex;align-items:center;gap:8px;background:#151515;color:#fff;border:0;
  border-radius:999px;padding:10px 15px;font-size:12.5px;font-weight:500;cursor:pointer;
  box-shadow:0 6px 22px rgba(0,0,0,.18);font-family:inherit}

.ca-bub .tab .d{width:16px;height:16px;flex:none;
  background:no-repeat center/contain url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Ccircle cx='12' cy='12' r='10.5' fill='none' stroke='%23A41034' stroke-width='2'/%3E%3Cpath d='M9.1 9.2a3 3 0 1 1 3.6 3.3v1.6' fill='none' stroke='%23A41034' stroke-width='2.1' stroke-linecap='round'/%3E%3Ccircle cx='12.1' cy='17.4' r='1.25' fill='%23A41034'/%3E%3C/svg%3E")}

.ca-bub .panel{width:max-content;min-width:284px;max-width:calc(100vw - 28px);
  background:#fff;border:1px solid var(--color-rule);border-radius:14px;
  box-shadow:0 14px 40px rgba(0,0,0,.16);overflow:hidden}
.ca-bub .x{position:absolute;top:8px;right:9px;background:0;border:0;font-size:17px;line-height:1;
  color:var(--color-ink-soft);cursor:pointer;padding:0 3px}
.ca-bub .bd{padding:14px 14px 12px;position:relative}
.ca-bub .q{font-size:13.5px;font-weight:500;letter-spacing:-.02em;margin:0 26px 10px 0;line-height:1.35;
  white-space:nowrap}
.ca-bub .opt{display:block;width:100%;text-align:left;background:#fff;border:1px solid var(--color-rule);
  border-radius:8px;padding:9px 11px;margin:0 0 6px;font-size:12.5px;cursor:pointer;
  font-family:inherit;color:var(--color-ink);line-height:1.4;white-space:nowrap}
.ca-bub .opt:hover{border-color:var(--color-ink)}
.ca-bub .back{background:0;border:0;color:var(--color-ink-soft);font-size:12px;cursor:pointer;padding:4px 0;font-family:inherit}
.ca-bub .go{display:block;background:var(--color-vermilion);color:#fff;border-radius:999px;padding:10px;
  text-align:center;text-decoration:none;font-size:12.5px;font-weight:500;margin:2px 0 6px}
.ca-bub .alt{display:block;text-align:center;font-size:11.5px;color:var(--color-ink-soft);text-decoration:none}
@media (max-width:640px){
  .ca-bub{left:14px;bottom:14px}
  
  .ca-bub .panel{max-width:calc(100vw - 28px)}
  
  .ca-bub .tab{font-size:13px;padding:11px 16px;gap:8px;max-width:calc(100vw - 28px)}
  .ca-bub .tab .d{width:19px;height:19px;flex:0 0 19px}
  .ca-bub .tab .words{white-space:nowrap}
}
`

/** Routes where the bubble stays out of the way. Its job is helping a man who
 *  does not know which program he wants; by /apply, /book, /pay or the
 *  membership checkout he has already decided, and a floating panel over the
 *  form is a distraction on the money path rather than help.
 *  /what-happens-next is the post-booking funnel page, which strips every
 *  send-him-back-to-comparing surface.
 *
 *  /community/join is listed because it is a checkout too: it takes the same
 *  chrome, so it takes the same silence. /community itself is NOT listed,
 *  because that page is exactly where a man still choosing should be able to
 *  reach the chooser. */
const HIDDEN = ["/apply", "/book", "/pay", "/what-happens-next", "/community/join"]

export function RoutingBubble() {
  const pathname = usePathname()
  const [open, setOpen] = useState(false)
  const [stack, setStack] = useState<string[]>(["start"])
  const here = stack[stack.length - 1]
  const back = () => setStack((s) => s.slice(0, -1))

  if (HIDDEN.some((p) => pathname === p || pathname.startsWith(`${p}/`))) return null

  let inner
  if (here === "__talk") {
    // Book a call is the bold primary; email is the second choice (owner).
    inner = (
      <>
        <p className="q">Still not sure? Let&rsquo;s talk.</p>
        <Link className="go" href="/book" onClick={() => setOpen(false)}>Book a Call</Link>
        <a className="alt" href="mailto:hello@coldapproach.io" onClick={() => setOpen(false)}>Email us</a>
      </>
    )
  } else if (here.startsWith("/")) {
    // The button alone. No "X is the one" line above it (owner).
    inner = (
      <>
        <Link className="go" href={here} onClick={() => setOpen(false)}>{NAME[here]}</Link>
        <Link className="alt" href="/compare" onClick={() => setOpen(false)}>or compare everything</Link>
      </>
    )
  } else {
    const step = STEPS[here]
    inner = (
      <>
        <p className="q">{step.q}</p>
        {step.a.map(([label, next]) => (
          <button key={label} className="opt" onClick={() => setStack((s) => [...s, next])}>
            {label}
          </button>
        ))}
      </>
    )
  }

  return (
    <div className="ca-bub">
      <ModuleStyle css={CSS} />
      {open ? (
        <div className="panel">
          <div className="bd">
            <button className="x" aria-label="Close" onClick={() => setOpen(false)}>&times;</button>
            {inner}
            {stack.length > 1 ? (
              <button className="back" onClick={back}>&larr; back</button>
            ) : null}
          </div>
        </div>
      ) : (
        <button
          className="tab"
          aria-label="Find your coaching program"
          onClick={() => {
            setStack(["start"])
            setOpen(true)
          }}
        >
          <span className="d" />
          <span className="words">Find Your Coaching Program</span>
        </button>
      )}
    </div>
  )
}
